/** Placement maths for tooltips, popovers and menus. No positioning library is available. */

export type Placement = 'top' | 'right' | 'bottom' | 'left'
export type Align = 'start' | 'center' | 'end'

export interface FloatingRect {
  width: number
  height: number
}

export interface FloatingPosition {
  top: number
  left: number
  placement: Placement
}

const VIEWPORT_PADDING = 8

const place = (
  anchor: DOMRect,
  floating: FloatingRect,
  placement: Placement,
  align: Align,
  offset: number,
): { top: number; left: number } => {
  const alignAxis = (start: number, size: number, anchorSize: number): number => {
    if (align === 'start') return start
    if (align === 'end') return start + anchorSize - size
    return start + anchorSize / 2 - size / 2
  }

  switch (placement) {
    case 'top':
      return {
        top: anchor.top - floating.height - offset,
        left: alignAxis(anchor.left, floating.width, anchor.width),
      }
    case 'bottom':
      return {
        top: anchor.bottom + offset,
        left: alignAxis(anchor.left, floating.width, anchor.width),
      }
    case 'left':
      return {
        top: alignAxis(anchor.top, floating.height, anchor.height),
        left: anchor.left - floating.width - offset,
      }
    case 'right':
      return {
        top: alignAxis(anchor.top, floating.height, anchor.height),
        left: anchor.right + offset,
      }
  }
}

const OPPOSITE: Record<Placement, Placement> = {
  top: 'bottom',
  bottom: 'top',
  left: 'right',
  right: 'left',
}

const fits = (
  candidate: { top: number; left: number },
  floating: FloatingRect,
  viewport: { width: number; height: number },
): boolean =>
  candidate.top >= VIEWPORT_PADDING &&
  candidate.left >= VIEWPORT_PADDING &&
  candidate.top + floating.height <= viewport.height - VIEWPORT_PADDING &&
  candidate.left + floating.width <= viewport.width - VIEWPORT_PADDING

/**
 * Positions a fixed-position layer beside an anchor, flipping to the opposite side when the
 * preferred one is clipped and clamping to the viewport afterwards.
 */
export const computePosition = (
  anchor: DOMRect,
  floating: FloatingRect,
  preferred: Placement,
  align: Align = 'center',
  offset = 8,
): FloatingPosition => {
  const viewport = { width: window.innerWidth, height: window.innerHeight }

  let placement = preferred
  let candidate = place(anchor, floating, placement, align, offset)

  if (!fits(candidate, floating, viewport)) {
    const flipped = OPPOSITE[preferred]
    const alternative = place(anchor, floating, flipped, align, offset)
    if (fits(alternative, floating, viewport)) {
      placement = flipped
      candidate = alternative
    }
  }

  const top = Math.min(
    Math.max(candidate.top, VIEWPORT_PADDING),
    Math.max(VIEWPORT_PADDING, viewport.height - floating.height - VIEWPORT_PADDING),
  )
  const left = Math.min(
    Math.max(candidate.left, VIEWPORT_PADDING),
    Math.max(VIEWPORT_PADDING, viewport.width - floating.width - VIEWPORT_PADDING),
  )

  return { top, left, placement }
}
